import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./CenterSearchBar.module.sass";
import TextInput from "../TextInput";
import Icon from "../Icon";
import { searchCenters, getCenterById } from "../../services/center.service";

const CenterSearchBar = ({ 
  className,
  label = "Rechercher un centre de formation",
  placeholder = "Nom du centre (min. 2 caractères)",
  onSelectCenter,
  showDetails = true,
  required = false,
  initialCenterId = null
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // Charger le centre initial si un ID est fourni
  useEffect(() => {
    const loadInitialCenter = async () => {
      if (initialCenterId) {
        try {
          const center = await getCenterById(initialCenterId);
          if (center) {
            setSelectedCenter(center);
            setSearchQuery(center.name || '');
          }
        } catch (error) {
          console.error('Erreur lors du chargement du centre:', error);
        }
      } else {
        // Réinitialiser si aucun ID n'est fourni
        setSelectedCenter(null);
        setSearchQuery('');
      }
    };

    loadInitialCenter();
  }, [initialCenterId]);

  // Effectuer la recherche lorsque la requête change
  useEffect(() => {
    const fetchCenters = async () => {
      if (searchQuery.length >= 2) {
        try {
          const results = await searchCenters(searchQuery);
          setSearchResults(results);
          setShowResults(true);
        } catch (error) {
          console.error('Erreur lors de la recherche des centres:', error);
          setSearchResults([]);
          setShowResults(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    };
    
    fetchCenters();
  }, [searchQuery]);

  // Gérer la sélection d'un centre
  const handleSelectCenter = (center) => {
    setSelectedCenter(center);
    setSearchQuery(center.name);
    setShowResults(false);
    
    // Transmettre le centre sélectionné au composant parent
    if (onSelectCenter) {
      onSelectCenter(center);
    }
  };

  // Gérer le changement dans la barre de recherche
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if (selectedCenter && e.target.value !== selectedCenter.name) {
      setSelectedCenter(null);
      if (onSelectCenter) {
        onSelectCenter(null);
      }
    }
  };

  // Gérer le vidage de la barre de recherche
  const handleClearSearch = () => {
    setSearchQuery("");
    setSelectedCenter(null);
    setShowResults(false);
    if (onSelectCenter) {
      onSelectCenter(null);
    }
  };

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.searchContainer}>
        <div className={styles.inputWrapper}>
          <TextInput
            className={styles.field}
            label={label}
            name="center-search"
            type="text"
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleSearchChange}
            required={required}
          />
          {searchQuery && (
            <button className={styles.clearButton} onClick={handleClearSearch} type="button" aria-label="Effacer la recherche">
              <Icon name="close-circle" size="20" />
            </button>
          )}  
        </div>
        
        {showResults && searchResults.length > 0 && (
          <div className={styles.searchResults}>
            {searchResults.map((center, index) => (
              <div 
                key={center.id || index} 
                className={styles.resultItem}
                onClick={() => handleSelectCenter(center)}
              >
                <div className={styles.resultContent}>
                  <img
                    src={center.logoUrl || "/images/content/school_logo.png"}
                    alt={center.name}
                    className={styles.companyLogo}
                  />
                  <div className={styles.centerInfo}>
                    <div className={styles.centerName}>{center.name}</div>
                    <div className={styles.centerDetails}>
                      <span>
                        {center.address}
                        {center.zip_code && center.city && `, ${center.zip_code} ${center.city}`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showResults && searchResults.length === 0 && searchQuery.length >= 2 && !selectedCenter && (
          <div className={styles.noResults}>
            Aucun centre de formation trouvé
          </div>
        )}
      </div>

      {selectedCenter && showDetails && (
        <div className={styles.selectedCenterDetails}>
          <h3>Détails du centre sélectionné</h3>
          <div className={styles.detailRow}>
            <span className={styles.label}>Nom:</span>
            <span className={styles.value}>{selectedCenter.name}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.label}>Adresse:</span>
            <span className={styles.value}>
              {selectedCenter.address}
              {selectedCenter.address_complement && `, ${selectedCenter.address_complement}`}
              {selectedCenter.zip_code && selectedCenter.city && `, ${selectedCenter.zip_code} ${selectedCenter.city}`}
            </span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.label}>Téléphone:</span>
            <span className={styles.value}>{selectedCenter.phone || 'Non renseigné'}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.label}>Email:</span>
            <span className={styles.value}>{selectedCenter.email || 'Non renseigné'}</span>
          </div>
          {selectedCenter.department && (
            <div className={styles.detailRow}>
              <span className={styles.label}>Département:</span>
              <span className={styles.value}>{selectedCenter.department}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CenterSearchBar;
